import React, { useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Line } from "@react-three/drei";
import * as THREE from "three";
import { T, MEJA, RAPAT, kursiRapat } from "./office";
import { OfficeRoom } from "./OfficeRoom";
import { Karakter, papan } from "./Karakter";

// Kantor isometrik khas Jawsel (R3F, kamera sudut iso 45° dengan fov sempit ≈ ortografik — pan & zoom saja, tanpa rotasi).
// Ruangan + perabot di OfficeRoom, karakter NPC interaktif di Karakter. Klik avatar → onPilih(key) dan kamera meluncur ke mejanya.
const ARAH = new THREE.Vector3(38, 34, 36).normalize();
const DEFAULT_TARGET = new THREE.Vector3(6.5, 0, 0.5), JARAK_DEFAULT = 78, JARAK_FOKUS = 17;

function KameraRig({ fokus }) {
  const { camera, controls } = useThree();
  const tujuan = useRef(new THREE.Vector3()), posisi = useRef(new THREE.Vector3()), arah = useRef(new THREE.Vector3()), aktif = useRef(false), terakhir = useRef(undefined);
  useFrame((_, dt) => {
    if (terakhir.current !== fokus && controls) {
      terakhir.current = fokus;
      const p = fokus && papan[fokus], m = fokus === "rapat" ? RAPAT.pos : fokus && (p ? [p.x, p.z] : MEJA[fokus]); // ke meja rapat / posisi agen saat ini
      // pertahankan arah pandang kamera saat ini (pengguna mungkin sudah memutar), hanya ubah target & jarak
      arah.current.copy(camera.position).sub(controls.target).normalize();
      tujuan.current.set(m ? m[0] : DEFAULT_TARGET.x, m ? 0.8 : DEFAULT_TARGET.y, m ? m[1] : DEFAULT_TARGET.z);
      posisi.current.copy(tujuan.current).addScaledVector(arah.current, m ? (fokus === "rapat" ? JARAK_FOKUS * 1.6 : JARAK_FOKUS) : JARAK_DEFAULT);
      aktif.current = true;
    }
    if (!aktif.current || !controls) return;
    const k = 1 - Math.exp(-dt * 4);
    camera.position.lerp(posisi.current, k);
    controls.target.lerp(tujuan.current, k);
    controls.update();
    if (camera.position.distanceTo(posisi.current) < 0.03 && controls.target.distanceTo(tujuan.current) < 0.03) aktif.current = false;
  });
  return null;
}

function Handoff({ dari, ke }) {
  const p = useRef();
  const a = MEJA[dari], b = MEJA[ke];
  useFrame(({ clock }) => { if (p.current) { const t = (clock.getElapsedTime() * .6) % 1; p.current.position.set(a[0] + (b[0] - a[0]) * t, 1.4 + Math.sin(t * Math.PI) * .7, a[1] + (b[1] - a[1]) * t); } });
  return (
    <>
      <Line points={[[a[0], 1.4, a[1]], [(a[0] + b[0]) / 2, 2.2, (a[1] + b[1]) / 2], [b[0], 1.4, b[1]]]} color={T.accent} lineWidth={1.5} dashed dashSize={.3} gapSize={.2} transparent opacity={.85} />
      <mesh ref={p}><sphereGeometry args={[0.1, 12, 12]} /><meshBasicMaterial color={T.accent} /></mesh>
    </>
  );
}

export function Office3D({ agen, dipilih, onPilih, rapat, onRapat, fokus }) {
  const kursi = useMemo(() => Object.fromEntries((rapat?.peserta || []).map((k, i) => [k, kursiRapat(i)])), [rapat?.id, rapat?.peserta?.join()]); // eslint-disable-line
  const handoffs = useMemo(() => agen.filter(a => a.status === "handoff" && a.key !== "orchestrator").map(a => a.key), [agen]);
  const orchHandoff = agen.find(a => a.key === "orchestrator")?.status === "handoff";
  const target = orchHandoff && agen.find(a => a.status === "working" && a.key !== "orchestrator")?.key;
  const awal = useMemo(() => DEFAULT_TARGET.clone().addScaledVector(ARAH, JARAK_DEFAULT).toArray(), []);
  return (
    <Canvas dpr={[1, 1.5]} shadows camera={{ position: awal, fov: 20, near: 1, far: 400 }} style={{ background: T.bg }} onPointerMissed={() => onPilih(null)} data-testid="office-canvas">
      <ambientLight intensity={.7} />
      <directionalLight position={[12, 22, 10]} intensity={1.1} castShadow shadow-mapSize={[1024, 1024]} shadow-camera-left={-24} shadow-camera-right={24} shadow-camera-top={24} shadow-camera-bottom={-24} shadow-bias={-0.0005} />
      <hemisphereLight args={["#ffffff", "#e7dfc8", .4]} />
      <OfficeRoom agen={agen} rapatAktif={!!rapat} onRapat={onRapat} />
      {agen.map(a => MEJA[a.key] && <Karakter key={a.key} a={a} dipilih={dipilih === a.key} onPilih={onPilih} kursi={kursi[a.key]} />)}
      {handoffs.map(k => <Handoff key={k} dari={k} ke="orchestrator" />)}
      {target && <Handoff dari="orchestrator" ke={target} />}
      <OrbitControls makeDefault enableDamping enableRotate enablePan minDistance={10} maxDistance={120} minPolarAngle={0.35} maxPolarAngle={1.25} screenSpacePanning={false} target={DEFAULT_TARGET.toArray()} mouseButtons={{ LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }} />
      <KameraRig fokus={fokus} />
    </Canvas>
  );
}
